/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React from 'react'
import { View, ActionButton } from '@components/atoms'
import {
	GridToolbarContainer,
	GridSlotProps,
	DEFAULT_GRID_AUTOSIZE_OPTIONS,
	gridPaginatedVisibleSortedGridRowIdsSelector,
	GridCsvGetRowsToExportParams,
	GridPreferencePanelsValue,
	GridPrintGetRowsToExportParams,
} from '@mui/x-data-grid'

import { useAppSelector } from '@redux/index'
import { grey } from '@mui/material/colors'

type PropsType = GridSlotProps['toolbar'] & {
	onAddClick?: () => void
}

export const GridToolBar = (props: PropsType) => {
	const { isLoading, apiRef } = props

	const userLanguage: string = useAppSelector(state => state.user.language)

	const addTitle = userLanguage === 'ko' ? '추가' : 'ADD'
	const autoSizeColumnsTitle = userLanguage === 'ko' ? '자동간격맞춤' : 'Autosize Columns'
	const includeHeaders = DEFAULT_GRID_AUTOSIZE_OPTIONS.includeHeaders
	const includeOutliers = DEFAULT_GRID_AUTOSIZE_OPTIONS.includeOutliers
	const outliersFactor = String(DEFAULT_GRID_AUTOSIZE_OPTIONS.outliersFactor)
	const expand = DEFAULT_GRID_AUTOSIZE_OPTIONS.expand

	const onAddClick = () => {
		if (typeof props.onAddClick === 'function') {
			props.onAddClick()
		}
	}

	const autosizeOptions = {
		includeHeaders,
		includeOutliers,
		outliersFactor: Number.isNaN(parseFloat(outliersFactor))
			? 1
			: parseFloat(outliersFactor),
		expand,
	}

	const onColumnClick = () => {
		apiRef.current?.showPreferences(GridPreferencePanelsValue.columns)
	}

	const onFilterClick = () => {
		apiRef.current?.showPreferences(GridPreferencePanelsValue.filters)
	}

	const onExportClick = () => {
		apiRef.current.exportDataAsCsv({
			getRowsToExport: ({ apiRef }: GridCsvGetRowsToExportParams) =>
				gridPaginatedVisibleSortedGridRowIdsSelector(apiRef),
		})
	}

	const onPrintClick = () => {
		apiRef.current.exportDataAsPrint({
			getRowsToExport: ({ apiRef }: GridPrintGetRowsToExportParams) =>
				gridPaginatedVisibleSortedGridRowIdsSelector(apiRef),
		})
	}

	return (
		<GridToolbarContainer>
			<View css={style}>
				{!isLoading && (
					<>
						<View className="leftToolView">
							<ActionButton
								iconName="plus"
								title={addTitle}
								buttonColor="inherit"
								iconTextColor={grey['900']}
								onClick={onAddClick}
							/>

							<ActionButton
								iconName="view-column"
								title={apiRef.current.getLocaleText('toolbarColumns')}
								buttonColor="inherit"
								iconTextColor={grey['900']}
								onClick={onColumnClick}
							/>

							<ActionButton
								iconName="filter"
								title={apiRef.current.getLocaleText('toolbarFilters')}
								buttonColor="inherit"
								iconTextColor={grey['900']}
								onClick={onFilterClick}
							/>

							<ActionButton
								iconName="file-download-outline"
								title={apiRef.current.getLocaleText('toolbarExport')}
								buttonColor="inherit"
								iconTextColor={grey['900']}
								onClick={onExportClick}
							/>
							<ActionButton
								iconName="printer"
								title={apiRef.current.getLocaleText('toolbarExportPrint')}
								buttonColor="inherit"
								iconTextColor={grey['900']}
								onClick={onPrintClick}
							/>
						</View>
						<View className="leftToolView">
							<ActionButton
								iconName="arrow-expand-horizontal"
								title={autoSizeColumnsTitle}
								buttonColor="inherit"
								iconTextColor={grey['900']}
								onClick={() => apiRef.current.autosizeColumns(autosizeOptions)}
							/>
						</View>
					</>
				)}
			</View>
		</GridToolbarContainer>
	)
}

const style = css({
	display: 'flex',
	justifyContent: 'space-between',
	width: '100%',
	paddingBottom: '0.3rem',
	borderBottom: '1px solid',
	borderColor: grey[200],

	'.leftToolView': {
		display: 'flex',
		justifyContent: 'flex-end',
	},
})
